import { ChartFilterTagBox as TagBox } from './ChartFilterTagBox';
import { Popover } from 'devextreme-react/popover';

import { useState, useRef } from 'react';
import {
    Chart,
    Series,
    CommonSeriesSettings,
    ArgumentAxis,
    ValueAxis,
    Legend,
    Export,
    Tooltip,
    Title,
} from 'devextreme-react/chart';
import {
    DataGrid,
    Column,
    FilterRow,
    HeaderFilter,
    LoadPanel,
    Scrolling,
    Paging,
} from "devextreme-react/data-grid";
import { Close as CloseIcon } from "@mui/icons-material";
import {
    PieChart,
    Series as PieSeries,
    Label,
    Connector,
    Legend as PieLegend,
    Tooltip as PieTooltip,
    Export as PieExport,
} from 'devextreme-react/pie-chart';
import Popup from 'devextreme-react/popup';
import { DeliverableColumns } from '../../../constants';
import { filterReportingRows, handleDeliverablesPointClick } from '../FSServices';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import DatasetLinkedIcon from '@mui/icons-material/DatasetLinked';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
    rawDeliverablesData: any[];
    onRefresh?: () => void;
    siteId: number;
    lastRefreshTime?: string;
    title?: string;
}

const bluePalette = [
    "#005B99",
    "#0077C8",
    "#009CDE", // Base color
    "#1AA6E8",
    "#33B2F0",
    "#4DBEF5",
    "#66C9F8",
    "#80D4FB",
    "#99DEFC",
    "#B3E8FD",
    "#CCEFFE",
    "#E6F8FF",
];

// ─── Formatters ───────────────────────────────────────────────────────────────

function customizeBarTooltip(arg: { argument: string; seriesName: string; valueText: string }) {
    return { text: `${arg.argument}\n${arg.seriesName}: ${arg.valueText}` };
}

function customizeCountTooltip(arg: { argument: string; valueText: string }) {
    return { text: `${arg.argument}\nCount: ${arg.valueText}` };
}

function customizePieTooltip(arg: { argument: string; value: number; percentText: string }) {
    return { text: `${arg.argument}: ${arg.value} (${arg.percentText})` };
}

function customizePieLabel(arg: { argument: string; percentText: string }) {
    return `${arg.argument}: ${arg.percentText}`;
}

function getEntityLegendLabel(fullText: string) {
    const normalized = String(fullText || '').trim();
    if (!normalized) return '';

    const numberMatches = normalized.match(/\d+/g);
    if (numberMatches && numberMatches.length > 0) {
        return numberMatches[numberMatches.length - 1];
    }

    return normalized.length > 3 ? normalized.slice(-3) : normalized;
}

function customizePieLegendText(arg: any) {
    const name = String(arg?.pointName || arg?.argument || '');
    const shortName = getEntityLegendLabel(name);
    const value = arg?.pointValueText || arg?.valueText || arg?.value;
    if (value === undefined || value === null || value === '') return shortName;
    return `${shortName} (${value})`;
}

function customizeEntityLegendText(arg: any) {
    const fullText = String(arg?.seriesName || arg?.text || arg?.pointName || arg?.argument || '');
    return getEntityLegendLabel(fullText);
}

function customizeEntityLegendHint(arg: any) {
    return String(arg?.seriesName || arg?.text || arg?.pointName || arg?.argument || '');
}

function getLegendTargetKey(event: any) {
    return event?.target?.name || event?.target?.argument || event?.target?.originalArgument || null;
}

function getLegendItemKey(item: any) {
    return item?.argument !== undefined && item?.argument !== null
        ? String(item.argument)
        : String(item?.text || item?.pointName || item?.name || '');
}

function resolveEntityName(item: any) {
    return item?.entity || item?.entityName || item?.[DeliverableColumns.ENTITY_NAME] || '';
}

function getRowStatusValues(item: any): string[] {
    return String(item?.status || item?.[DeliverableColumns.STATUS] || '')
        .split(',')
        .map((s: string) => s.trim())
        .filter(Boolean);
}

function getRowFundValue(item: any): string {
    return String(item?.fund || item?.[DeliverableColumns.FUND] || '').trim();
}

const LEGEND_POPUP_MAX_ROWS = 15;

function splitLegendItemsIntoColumns(items: string[], maxRows = LEGEND_POPUP_MAX_ROWS) {
    const columns: string[][] = [];
    for (let i = 0; i < items.length; i += maxRows) {
        columns.push(items.slice(i, i + maxRows));
    }
    return columns;
}

function getLegendPopupItemColor(columnIndex: number, itemIndex: number, maxRows = LEGEND_POPUP_MAX_ROWS) {
    const paletteIndex = (columnIndex * maxRows) + itemIndex;
    return bluePalette[paletteIndex % bluePalette.length];
}

// ─── Pie Legend Toggle Hook ───────────────────────────────────────────────────

function usePieLegendSliceToggle(visibleLegendKeys?: Set<string>) {
    const [selectedStatuses, setSelectedStatuses] = useState<Set<string>>(new Set());
    const chartInstanceRef = useRef<any>(null);

    const setChartComponent = (component: any) => {
        if (component) chartInstanceRef.current = component;
    };

    const applySelectionToPoints = (nextSelection: Set<string>, component: any) => {
        const series = component?.getAllSeries?.()?.[0];
        if (!series || typeof series.getAllPoints !== 'function') return;

        const hasSelection = nextSelection.size > 0;
        series.getAllPoints().forEach((point: any) => {
            const pointKey = point?.argument !== undefined && point?.argument !== null
                ? String(point.argument)
                : String(point?.originalArgument || '');
            const shouldShow = !hasSelection || nextSelection.has(pointKey);
            shouldShow ? point.show?.() : point.hide?.();
        });
    };

    const toggleStatus = (status: string, component?: any) => {
        setSelectedStatuses((prev) => {
            const next = new Set(prev);
            next.has(status) ? next.delete(status) : next.add(status);
            applySelectionToPoints(next, component || chartInstanceRef.current);
            return next;
        });
    };

    const handleLegendClick = (event: any) => {
        event.cancel = true;
        setChartComponent(event?.component);

        const rawTarget = event?.target;
        const status = getLegendTargetKey(event) ||
            (rawTarget !== undefined && rawTarget !== null ? String(rawTarget) : null);
        if (!status) return;

        toggleStatus(status, event?.component);
    };

    const customizeLegendItems = (items: any[]) =>
        items
            .filter((item) => {
                if (!visibleLegendKeys || visibleLegendKeys.size === 0) return true;
                return visibleLegendKeys.has(getLegendItemKey(item));
            })
            .map((item) => {
                const itemKey = getLegendItemKey(item);
                if (selectedStatuses.size === 0 || selectedStatuses.has(itemKey)) return item;
                return {
                    ...item,
                    marker: { ...item.marker, opacity: 0.35 },
                    font: { ...item.font, color: '#888b8d' },
                };
            });

    return {
        handleLegendClick,
        customizeLegendItems,
        toggleStatus,
        isStatusSelected: (status: string) => selectedStatuses.size === 0 || selectedStatuses.has(status),
        setChartComponent,
    };
}

function useOverflowLegend(seriesNames: string[], maxVisibleLegendItems = 5, legendColumns = 6) {
    const isLegendOverflowing = seriesNames.length > maxVisibleLegendItems;
    const visibleLegendSeries = isLegendOverflowing
        ? seriesNames.slice(0, maxVisibleLegendItems)
        : seriesNames;
    const legendRows = Math.max(1, Math.ceil(visibleLegendSeries.length / legendColumns));
    const [isLegendPopupOpen, setIsLegendPopupOpen] = useState(false);

    return {
        legendColumns,
        maxVisibleLegendItems,
        isLegendOverflowing,
        legendRows,
        isLegendPopupOpen,
        setIsLegendPopupOpen,
    };
}

// ─── Data Helpers ─────────────────────────────────────────────────────────────

function getPivotedData(rawData: any[]) {
    const pivotedMap: Record<string, any> = {};
    rawData.forEach((item) => {
        const entity = resolveEntityName(item);
        const federalReturnType = item.federalReturnType || '';
        const statuses = getRowStatusValues(item);

        statuses.forEach((status: string) => {
            if (!pivotedMap[status]) {
                pivotedMap[status] = {
                    status,
                    entities: new Set<string>(),
                    federalReturnTypes: new Set<string>(),
                };
            }

            pivotedMap[status].entities.add(entity);
            pivotedMap[status].federalReturnTypes.add(federalReturnType);

            pivotedMap[status][entity] = (pivotedMap[status][entity] || 0) + 1;
        });

        if (statuses.length === 0) {
            if (!pivotedMap['Unknown Status']) {
                pivotedMap['Unknown Status'] = {
                    status: 'Unknown Status',
                    entities: new Set<string>(),
                    federalReturnTypes: new Set<string>(),
                };
            }

            pivotedMap['Unknown Status'].entities.add(entity);
            pivotedMap['Unknown Status'].federalReturnTypes.add(federalReturnType);

            pivotedMap['Unknown Status'][entity] =
                (pivotedMap['Unknown Status'][entity] || 0) + 1;
        }
    });

    return Object.values(pivotedMap)
        .map((item: any) => ({
            ...item,
            entityName: Array.from(item.entities || [])[0] || '',
            federalReturnType: Array.from(item.federalReturnTypes || [])[0] || undefined,
            entities: undefined,
            federalReturnTypes: undefined,
        }))
        .sort((a: any, b: any) =>
            String(a.status || '').localeCompare(String(b.status || ''), undefined, { numeric: true })
        );
}

function getEntitySeries(rawData: any[]) {
    const entities = new Set<string>();
    rawData.forEach((item) => {
        entities.add(resolveEntityName(item));
    });
    return Array.from(entities);
}

function getFundGroupedData(rawData: any[]) {
    const grouped: Record<string, any> = {};
    rawData.forEach((x: any) => {
        const fund = getRowFundValue(x) || '';
        if (!grouped[fund]) {
            grouped[fund] = {
                fund,
                entities: new Set<string>(),
            };
        }
        const entity = resolveEntityName(x);
        grouped[fund].entities.add(entity);

        grouped[fund][entity] = (grouped[fund][entity] || 0) + 1;
    });
    return Object.values(grouped)
        .map(item => ({
            ...item,
            entityName: Array.from(item.entities)[0] || '',
            entities: undefined
        }))
        .sort((a: any, b: any) => String(a.fund || '').localeCompare(String(b.fund || '')));
}

function getStatusCountData(rawData: any[]) {
    const grouped: Record<string, { entities: Set<string>; counts: Record<string, number> }> = {};
    rawData.forEach((x: any) => {
        const federalDueDate = x.federalDueDate || x.federalReturnDueDate;
        if (!federalDueDate) return;
        const entity = resolveEntityName(x);
        const parsedDate = new Date(federalDueDate);
        const dueDateLabel = Number.isNaN(parsedDate.getTime())
            ? String(federalDueDate)
            : parsedDate.toLocaleDateString('en-US');
        if (!grouped[dueDateLabel]) {
            grouped[dueDateLabel] = { entities: new Set<string>(), counts: {} };
        }
        grouped[dueDateLabel].entities.add(entity);
        grouped[dueDateLabel].counts[entity] = (grouped[dueDateLabel].counts[entity] || 0) + 1;
    });
    const dueDates = Object.keys(grouped);
    const chartData = dueDates.map((dueDate) => ({
        federalDueDate: dueDate,
        entityName: Array.from(grouped[dueDate].entities)[0] || '',
        ...grouped[dueDate].counts,
    }));
    return { chartData, dueDates };
}

function getStatusBeforeDate(rawData: any[], cutoffDate: Date) {
    const filtered = rawData.filter((x: any) =>
        x.targetedReturnDeliveryDate && new Date(x.targetedReturnDeliveryDate) < cutoffDate
    );
    const grouped: Record<string, number> = {};
    filtered.forEach((x: any) => {
        const entity = resolveEntityName(x);
        grouped[entity] = (grouped[entity] || 0) + 1;
    });
    return Object.keys(grouped).map((entityName) => ({
        entityName,
        value: grouped[entityName],
    }));
}

function getStatusGrouped(rawData: any[]) {
    const grouped: Record<string, number> = {};
    rawData.forEach((x: any) => {
        const entity = resolveEntityName(x);
        grouped[entity] = (grouped[entity] || 0) + 1;
    });
    return Object.keys(grouped).map((entityName) => ({
        entityName,
        value: grouped[entityName],
    }));
}

function getUniqueValues(rawData: any[], field: string): string[] {
    return Array.from(new Set(rawData.map((row) => row[field]).filter(Boolean))).sort();
}

function getEntityFilterValues(rawData: any[]): string[] {
    return Array.from(new Set(rawData.map((row) => resolveEntityName(row)).filter(Boolean))).sort();
}

function getStatusFilterValues(rawData: any[]): string[] {
    const statuses = rawData.flatMap((row) => getRowStatusValues(row));
    return Array.from(new Set(statuses)).sort();
}

function getFundFilterValues(rawData: any[]): string[] {
    return Array.from(new Set(rawData.map((row) => getRowFundValue(row)).filter(Boolean))).sort();
}

function getDueDateFilterValues(rawData: any[]): string[] {
    const labels = rawData
        .map((row) => {
            const rawDueDate = row.federalDueDate || row.federalReturnDueDate;
            if (!rawDueDate) return null;
            const parsedDate = new Date(rawDueDate);
            return Number.isNaN(parsedDate.getTime())
                ? String(rawDueDate)
                : parsedDate.toLocaleDateString('en-US');
        })
        .filter(Boolean) as string[];
    return Array.from(new Set(labels)).sort();
}

function getDeliverableDueDateValue(row: any) {
    const rawDueDate = row?.federalDueDate || row?.federalReturnDueDate;
    if (!rawDueDate) return '';
    const parsedDate = new Date(rawDueDate);
    return Number.isNaN(parsedDate.getTime()) ? String(rawDueDate) : parsedDate.toLocaleDateString('en-US');
}

function DeliverablesDataPointsDialog(props: { open: boolean; onClose: () => void; title: string; rows: any[] }) {

    const dataViewRows = props.rows.map((row: any) => {
        const statuses = getRowStatusValues(row);
        const statusText = statuses.length > 0 ? statuses.join(', ') : String(row?.status || '');
        return {
            status: statusText,
            entity: resolveEntityName(row),
            fund: getRowFundValue(row) || '',
            dueDate: getDeliverableDueDateValue(row),
            federalDueDate: String(row?.federalDueDate || row?.federalDueDate || row?.federalDueDate),
            federalExtendedDueDate: String(row?.federalExtendedDueDate || row?.federalExtendedDueDate),
        };
    });

    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            maxWidth="lg"
            fullWidth
        >

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 2,
                    py: 1,
                    borderBottom: "1px solid #e0e0e0",
                }}
            >
                <Typography variant="h6">
                    {props.title}
                </Typography>

                <IconButton
                    aria-label="close"
                    onClick={props.onClose}
                    size="small"
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent sx={{ p: 2 }}>
                <DataGrid
                    dataSource={dataViewRows}
                    height={500}
                    showBorders
                    rowAlternationEnabled
                    columnAutoWidth
                    wordWrapEnabled
                    hoverStateEnabled
                    noDataText="No data available"
                >
                    <LoadPanel enabled={false} />

                    <FilterRow visible />

                    <HeaderFilter visible />

                    <Paging
                        enabled
                        defaultPageSize={20}
                    />

                    <Scrolling mode="standard" />

                    <Column
                        dataField="status"
                        caption="Status"
                    />

                    <Column
                        dataField="entity"
                        caption="Entity"
                    />

                    <Column
                        dataField="fund"
                        caption="Fund"
                    />

                    <Column
                        dataField="dueDate"
                        caption="Due Date"
                    />

                    <Column
                        dataField="federalDueDate"
                        caption="Federal Due Date"
                    />

                    <Column
                        dataField="federalExtendedDueDate"
                        caption="Federal Extended Due Date"
                    />
                </DataGrid>
            </DialogContent>
        </Dialog>
    );

}

// ─── Sub Charts ───────────────────────────────────────────────────────────────

export function FormStatusByEntityChart({ rawDeliverablesData, siteId, onRefresh, lastRefreshTime, title }: Props) {
    const [selectedSeries, setSelectedSeries] = useState<string[]>([]);
    const [entityFilter, setEntityFilter] = useState<string[]>([]);
    const [statusFilter, setStatusFilter] = useState<string[]>([]);
    const [fundFilter, setFundFilter] = useState<string[]>([]);
    const [isFilterPopoverOpen, setIsFilterPopoverOpen] = useState(false);
    const [isDataViewOpen, setIsDataViewOpen] = useState(false);

    const sourceData = rawDeliverablesData ?? [];

    const filters = {
        entity: (row: any) => entityFilter.length === 0 || entityFilter.includes(resolveEntityName(row)),
        status: (row: any) => statusFilter.length === 0 || getRowStatusValues(row).some((status) => statusFilter.includes(status)),
        fund: (row: any) => fundFilter.length === 0 || fundFilter.includes(getRowFundValue(row)),
    };
    const entityOptions = getEntityFilterValues(filterReportingRows(sourceData, filters, 'entity'));
    const statusOptions = getStatusFilterValues(filterReportingRows(sourceData, filters, 'status'));
    const fundOptions = getFundFilterValues(filterReportingRows(sourceData, filters, 'fund'));
    const filteredData = filterReportingRows(sourceData, filters);

    const data = getPivotedData(filteredData);
    const entitySeries = getEntitySeries(filteredData);
    const {
        legendColumns,
        maxVisibleLegendItems,
        isLegendOverflowing,
        legendRows,
        isLegendPopupOpen,
        setIsLegendPopupOpen,
    } = useOverflowLegend(entitySeries);

    const handleLegendClick = (event: any) => {
        event.cancel = true;
        const legendKey = getLegendTargetKey(event);
        if (!legendKey) return;
        setSelectedSeries((curr) =>
            curr.includes(legendKey) ? curr.filter((n) => n !== legendKey) : [...curr, legendKey]
        );
    };

    const handlePointClick = (event: any) =>
        handleDeliverablesPointClick(event, siteId, DeliverableColumns.STATUS, DeliverableColumns.ENTITY_NAME, rawDeliverablesData);

    const activeFilterCount =
        (entityFilter.length > 0 ? 1 : 0) + (statusFilter.length > 0 ? 1 : 0) + (fundFilter.length > 0 ? 1 : 0);

    const clearAllFilters = () => {
        setEntityFilter([]);
        setStatusFilter([]);
        setFundFilter([]);
    };

    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 8,
                    width: '100%',
                }}
            >
                <div>
                    <div className="chart-title-row1">
                        <h3 className="chart-title1">{title}</h3>
                    </div>
                    {lastRefreshTime && <span className="chart-refresh-time1">
                        Last Refreshed: {lastRefreshTime}
                    </span>}
                </div>

                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                    }}
                >
                    <div
                        onClick={() => setIsDataViewOpen(true)}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 32,
                            height: 32,
                            borderRadius: 4,
                            border: '1px solid #d0d0d0',
                            cursor: 'pointer',
                            backgroundColor: '#fff',
                        }}
                        title="Data Points"
                    >
                        <DatasetLinkedIcon sx={{ fontSize: 16, color: '#555' }} />
                    </div>
                    <div
                        id="chart-filter-icon-trigger"
                        onClick={() => setIsFilterPopoverOpen((curr) => !curr)}
                        className={`fs-chart-filter-trigger${activeFilterCount > 0 ? ' fs-chart-filter-trigger--active' : ''}`}
                        title="Filters"
                    >
                        <i
                            className="dx-icon-filter"
                            style={{ fontSize: 16, color: '#555' }}
                            aria-hidden="true"
                        />
                        {activeFilterCount > 0 && (
                            <span
                                style={{
                                    position: 'absolute',
                                    top: -4,
                                    right: -4,
                                    minWidth: 16,
                                    height: 16,
                                    borderRadius: 8,
                                    backgroundColor: '#1976d2',
                                    color: '#fff',
                                    fontSize: 10,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '0 3px',
                                }}
                            >
                                {activeFilterCount}
                            </span>
                        )}
                    </div>

                    {onRefresh && (
                        <div
                            onClick={onRefresh}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 32,
                                height: 32,
                                borderRadius: 4,
                                border: '1px solid #d0d0d0',
                                cursor: 'pointer',
                                backgroundColor: '#fff',
                            }}
                            title="Refresh"
                        >
                            <i
                                className="dx-icon-refresh"
                                style={{ fontSize: 16, color: '#555' }}
                                aria-hidden="true"
                            />
                        </div>
                    )}
                </div>
            </div>

            <Popover
                target="#chart-filter-icon-trigger"
                visible={isFilterPopoverOpen}
                onHiding={() => setIsFilterPopoverOpen(false)}
                hideOnOutsideClick={true}
                position="bottom"
                width={280}
            >
                <div className="fs-chart-filter-content" style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 4 }}>
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Entity</div>
                        <TagBox
                            dataSource={entityOptions}
                            value={entityFilter}
                            onValueChanged={(e) => setEntityFilter(e.value ?? [])}
                            placeholder="All entities"
                            showSelectionControls={true}
                            applyValueMode="instantly"
                            searchEnabled={true}
                            width="100%"
                        />
                    </div>

                    {/* <div>
                        <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Status</div>
                        <TagBox
                            dataSource={statusOptions}
                            value={statusFilter}
                            onValueChanged={(e) => setStatusFilter(e.value ?? [])}
                            placeholder="All statuses"
                            showSelectionControls={true}
                            applyValueMode="instantly"
                            searchEnabled={true}
                            width="100%"
                        />
                    </div> */}

                    <div>
                        <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Fund</div>
                        <TagBox
                            dataSource={fundOptions}
                            value={fundFilter}
                            onValueChanged={(e) => setFundFilter(e.value ?? [])}
                            placeholder="All funds"
                            showSelectionControls={true}
                            applyValueMode="instantly"
                            searchEnabled={true}
                            width="100%"
                        />
                    </div>

                    {activeFilterCount > 0 && (
                        <button
                            type="button"
                            onClick={clearAllFilters}
                            style={{
                                fontSize: 12,
                                alignSelf: 'flex-start',
                                background: 'none',
                                border: 'none',
                                color: '#1976d2',
                                cursor: 'pointer',
                                padding: 0,
                            }}
                        >
                            Clear all filters
                        </button>
                    )}
                </div>
            </Popover>

            <DeliverablesDataPointsDialog
                open={isDataViewOpen}
                onClose={() => setIsDataViewOpen(false)}
                title={`${title || 'Deliverables - Data Points'} `}
                rows={filteredData}
            />

            {data?.length > 0 ? (
                <>
                    <Chart id="form-status-bar" dataSource={data} rotated={true} onLegendClick={handleLegendClick} onPointClick={handlePointClick}>
                        <CommonSeriesSettings argumentField="status" type="stackedbar" />
                        {entitySeries.map((entityName, index) => (
                            <Series
                                key={entityName}
                                valueField={entityName}
                                name={entityName}
                                color={bluePalette[index % bluePalette.length]}
                                visible={selectedSeries.length === 0 || selectedSeries.includes(entityName)}
                                showInLegend={!isLegendOverflowing || index < maxVisibleLegendItems}
                            />
                        ))}

                        <ValueAxis position="left"><Title text="Count" /></ValueAxis>
                        <ArgumentAxis><Title text="Status" /></ArgumentAxis>

                        <Legend
                            verticalAlignment="bottom"
                            horizontalAlignment="center"
                            itemTextPosition="top"
                            columnCount={legendColumns}
                            rowCount={legendRows}
                            customizeText={customizeEntityLegendText}
                            customizeHint={customizeEntityLegendHint}
                        />

                        <Export enabled={false} />
                        <Tooltip enabled={true} customizeTooltip={customizeBarTooltip} />
                    </Chart>

                    {isLegendOverflowing && (
                        <div className="legend-more-wrapper" style={{ textAlign: 'center', marginTop: 4 }}>
                            <button type="button" className="legend-more-btn" onClick={() => setIsLegendPopupOpen(true)}>
                                +{entitySeries.length - maxVisibleLegendItems} more
                            </button>
                        </div>
                    )}

                    <Popup
                        visible={isLegendPopupOpen}
                        onHiding={() => setIsLegendPopupOpen(false)}
                        title="All Entities"
                        showCloseButton={true}
                        width={400}
                        height="auto"
                        dragEnabled={false}
                    >
                        <div className="legend-popup-list" style={{ display: 'flex', alignItems: 'flex-start', gap: 12, overflowX: 'auto' }}>
                            {splitLegendItemsIntoColumns(entitySeries).map((column, columnIndex) => (
                                <div key={`entity-col-${columnIndex}`} className="legend-popup-column" style={{ display: 'flex', flexDirection: 'column', minWidth: 220 }}>
                                    {column.map((entityName, itemIndex) => {
                                        const isSelected = selectedSeries.length === 0 || selectedSeries.includes(entityName);
                                        const legendColor = getLegendPopupItemColor(columnIndex, itemIndex);
                                        return (
                                            <div
                                                key={entityName}
                                                className="legend-popup-item"
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    padding: '6px 8px',
                                                    cursor: 'pointer',
                                                    opacity: isSelected ? 1 : 0.4,
                                                }}
                                                onClick={() =>
                                                    setSelectedSeries((curr) =>
                                                        curr.includes(entityName)
                                                            ? curr.filter((n) => n !== entityName)
                                                            : [...curr, entityName]
                                                    )
                                                }
                                            >
                                                <span
                                                    aria-hidden={true}
                                                    style={{
                                                        width: 10,
                                                        height: 10,
                                                        borderRadius: 2,
                                                        backgroundColor: legendColor,
                                                        marginRight: 8,
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <span>{entityName}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    </Popup>
                </>
            ) : (
                <div className="no-data-message">No data available</div>
            )}
        </div>
    );
}

export function FundStatusChart({ rawDeliverablesData, siteId, lastRefreshTime, onRefresh, title }: Props) {
    const [selectedSeries, setSelectedSeries] = useState<string[]>([]);
    const [entityFilter, setEntityFilter] = useState<string[]>([]);
    const [fundFilter, setFundFilter] = useState<string[]>([]);
    const [isFilterPopoverOpen, setIsFilterPopoverOpen] = useState(false);
    const [isDataViewOpen, setIsDataViewOpen] = useState(false);

    const sourceData = rawDeliverablesData ?? [];

    const filters = {
        entity: (row: any) => entityFilter.length === 0 || entityFilter.includes(resolveEntityName(row)),
        fund: (row: any) => fundFilter.length === 0 || fundFilter.includes(getRowFundValue(row)),
    };
    const entityOptions = getEntityFilterValues(filterReportingRows(sourceData, filters, 'entity'));
    const fundOptions = getFundFilterValues(filterReportingRows(sourceData, filters, 'fund'));
    const filteredData = filterReportingRows(sourceData, filters);

    const chartData = getFundGroupedData(filteredData);
    const entitySeries = getEntitySeries(filteredData);
    const {
        legendColumns,
        maxVisibleLegendItems,
        isLegendOverflowing,
        legendRows,
        isLegendPopupOpen,
        setIsLegendPopupOpen,
    } = useOverflowLegend(entitySeries);

    const handleLegendClick = (event: any) => {
        event.cancel = true;
        const legendKey = getLegendTargetKey(event);
        if (!legendKey) return;
        setSelectedSeries((curr) =>
            curr.includes(legendKey) ? curr.filter((n) => n !== legendKey) : [...curr, legendKey]
        );
    };

    const handlePointClick = (event: any) => handleDeliverablesPointClick(event, siteId, DeliverableColumns.FUND, DeliverableColumns.FEDERAL_EXTENDED_DUE_DATE, rawDeliverablesData);

    const activeFilterCount = (entityFilter.length > 0 ? 1 : 0) + (fundFilter.length > 0 ? 1 : 0);

    const clearAllFilters = () => {
        setEntityFilter([]);
        setFundFilter([]);
    };

    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    marginBottom: 8,
                }}
            >
                {/* Left Section */}
                <div>
                    <div className="chart-title-row1">
                        <h3 className="chart-title1">
                            {title}
                        </h3>
                    </div>
                    <span className="chart-refresh-time1">
                        Last Refreshed: {lastRefreshTime}
                    </span>
                </div>

                {/* Right Section */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                    }}
                >
                    <div
                        onClick={() => setIsDataViewOpen(true)}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 32,
                            height: 32,
                            borderRadius: 4,
                            border: '1px solid #d0d0d0',
                            cursor: 'pointer',
                            backgroundColor: '#fff',
                        }}
                        title="Data Points"
                    >
                        <DatasetLinkedIcon sx={{ fontSize: 16, color: '#555' }} />
                    </div>

                    <div
                        id="fund-filter-icon-trigger"
                        onClick={() => setIsFilterPopoverOpen((curr) => !curr)}
                        className={`fs-chart-filter-trigger${activeFilterCount > 0 ? ' fs-chart-filter-trigger--active' : ''}`}
                        title="Filters"
                    >
                        <i
                            className="dx-icon-filter"
                            style={{ fontSize: 16, color: '#555' }}
                            aria-hidden="true"
                        />

                        {activeFilterCount > 0 && (
                            <span
                                style={{
                                    position: 'absolute',
                                    top: -4,
                                    right: -4,
                                    minWidth: 16,
                                    height: 16,
                                    borderRadius: 8,
                                    backgroundColor: '#1976d2',
                                    color: '#fff',
                                    fontSize: 10,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '0 3px',
                                }}
                            >
                                {activeFilterCount}
                            </span>
                        )}
                    </div>
                    {onRefresh && (
                        <div
                            onClick={onRefresh}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 32,
                                height: 32,
                                borderRadius: 4,
                                border: '1px solid #d0d0d0',
                                cursor: 'pointer',
                                backgroundColor: '#fff',
                            }}
                            title="Refresh"
                        >
                            <i
                                className="dx-icon-refresh"
                                style={{ fontSize: 16, color: '#555' }}
                                aria-hidden="true"
                            />
                        </div>
                    )}
                </div>
            </div>

            <Popover
                target="#fund-filter-icon-trigger"
                visible={isFilterPopoverOpen}
                onHiding={() => setIsFilterPopoverOpen(false)}
                hideOnOutsideClick={true}
                position="bottom"
                width={280}
            >
                <div className="fs-chart-filter-content" style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 4 }}>
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Entity</div>
                        <TagBox
                            dataSource={entityOptions}
                            value={entityFilter}
                            onValueChanged={(e) => setEntityFilter(e.value ?? [])}
                            placeholder="All entities"
                            showSelectionControls={true}
                            applyValueMode="instantly"
                            searchEnabled={true}
                            width="100%"
                        />
                    </div>
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Fund</div>
                        <TagBox
                            dataSource={fundOptions}
                            value={fundFilter}
                            onValueChanged={(e) => setFundFilter(e.value ?? [])}
                            placeholder="All funds"
                            showSelectionControls={true}
                            applyValueMode="instantly"
                            searchEnabled={true}
                            width="100%"
                        />
                    </div>
                    {activeFilterCount > 0 && (
                        <button
                            type="button"
                            onClick={clearAllFilters}
                            style={{
                                fontSize: 12,
                                alignSelf: 'flex-start',
                                background: 'none',
                                border: 'none',
                                color: '#1976d2',
                                cursor: 'pointer',
                                padding: 0,
                            }}
                        >
                            Clear all filters
                        </button>
                    )}
                </div>
            </Popover>

            <DeliverablesDataPointsDialog
                open={isDataViewOpen}
                onClose={() => setIsDataViewOpen(false)}
                title={`${title || 'Deliverables - Data Points'} `}
                rows={filteredData}
            />

            {
                chartData?.length > 0 ? (
                    <>
                        <Chart id="deliverables-fund-chart" dataSource={chartData} rotated={true} onLegendClick={handleLegendClick} onPointClick={handlePointClick}>
                            <CommonSeriesSettings argumentField="fund" type="stackedbar" />
                            {entitySeries.map((entityName, index) => (
                                <Series
                                    key={entityName}
                                    valueField={entityName}
                                    name={entityName}
                                    color={bluePalette[index % bluePalette.length]}
                                    visible={selectedSeries.length === 0 || selectedSeries.includes(entityName)}
                                    showInLegend={!isLegendOverflowing || index < maxVisibleLegendItems}
                                />
                            ))}

                            <ValueAxis><Title text="Count" /></ValueAxis>
                            <ArgumentAxis><Title text="Fund" /></ArgumentAxis>
                            <Legend verticalAlignment="bottom" horizontalAlignment="center" itemTextPosition="top" columnCount={legendColumns} rowCount={legendRows} customizeText={customizeEntityLegendText} customizeHint={customizeEntityLegendHint} />
                            <Export enabled={false} />
                            <Tooltip enabled={true} customizeTooltip={customizeBarTooltip} />
                        </Chart>

                        {isLegendOverflowing && (
                            <div className="legend-more-wrapper" style={{ textAlign: 'center', marginTop: 4 }}>
                                <button
                                    type="button"
                                    className="legend-more-btn"
                                    onClick={() => setIsLegendPopupOpen(true)}
                                >
                                    +{entitySeries.length - maxVisibleLegendItems} more
                                </button>
                            </div>
                        )}

                        <Popup
                            visible={isLegendPopupOpen}
                            onHiding={() => setIsLegendPopupOpen(false)}
                            title="All Entities"
                            showCloseButton={true}
                            width={400}
                            height="auto"
                            dragEnabled={false}
                        >
                            <div className="legend-popup-list" style={{ display: 'flex', alignItems: 'flex-start', gap: 12, overflowX: 'auto' }}>
                                {splitLegendItemsIntoColumns(entitySeries).map((column, columnIndex) => (
                                    <div key={`fund-col-${columnIndex}`} className="legend-popup-column" style={{ display: 'flex', flexDirection: 'column', minWidth: 220 }}>
                                        {column.map((entityName, itemIndex) => {
                                            const isSelected = selectedSeries.length === 0 || selectedSeries.includes(entityName);
                                            const legendColor = getLegendPopupItemColor(columnIndex, itemIndex);
                                            return (
                                                <div
                                                    key={entityName}
                                                    className="legend-popup-item"
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        padding: '6px 8px',
                                                        cursor: 'pointer',
                                                        opacity: isSelected ? 1 : 0.4,
                                                    }}
                                                    onClick={() =>
                                                        setSelectedSeries((curr) =>
                                                            curr.includes(entityName)
                                                                ? curr.filter((n) => n !== entityName)
                                                                : [...curr, entityName]
                                                        )
                                                    }
                                                >
                                                    <span
                                                        aria-hidden={true}
                                                        style={{
                                                            width: 10,
                                                            height: 10,
                                                            borderRadius: 2,
                                                            backgroundColor: legendColor,
                                                            marginRight: 8,
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                    <span>{entityName}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ))}
                            </div>
                        </Popup>
                    </>
                ) : (
                    <div className="no-data-message">
                        No data available
                    </div>
                )
            }
        </div>
    );
}

export function StatusbyDueDate({ rawDeliverablesData, siteId, lastRefreshTime, onRefresh, title }: Props) {
    const [selectedSeries, setSelectedSeries] = useState<string[]>([]);
    const [entityFilter, setEntityFilter] = useState<string[]>([]);
    const [dueDateFilter, setDueDateFilter] = useState<string[]>([]);
    const [isFilterPopoverOpen, setIsFilterPopoverOpen] = useState(false);
    const [isDataViewOpen, setIsDataViewOpen] = useState(false);

    const sourceData = rawDeliverablesData ?? [];

    const filters = {
        entity: (row: any) => entityFilter.length === 0 || entityFilter.includes(resolveEntityName(row)),
        dueDate: (row: any) => dueDateFilter.length === 0 || dueDateFilter.includes(getDeliverableDueDateValue(row)),
    };
    const entityOptions = getEntityFilterValues(filterReportingRows(sourceData, filters, 'entity'));
    const dueDateOptions = getDueDateFilterValues(filterReportingRows(sourceData, filters, 'dueDate'));
    const filteredData = filterReportingRows(sourceData, filters);

    const { chartData } = getStatusCountData(filteredData);
    const entitySeries = getEntitySeries(filteredData);
    const {
        legendColumns,
        maxVisibleLegendItems,
        isLegendOverflowing,
        legendRows,
        isLegendPopupOpen,
        setIsLegendPopupOpen,
    } = useOverflowLegend(entitySeries);

    const handleLegendClick = (event: any) => {
        event.cancel = true;
        const legendKey = getLegendTargetKey(event);
        if (!legendKey) return;
        setSelectedSeries((curr) =>
            curr.includes(legendKey) ? curr.filter((n) => n !== legendKey) : [...curr, legendKey]
        );
    };

    const handlePointClick = (event: any) => handleDeliverablesPointClick(event, siteId, DeliverableColumns.FEDERAL_DUE_DATE, DeliverableColumns.FUND, rawDeliverablesData);

    const activeFilterCount = (entityFilter.length > 0 ? 1 : 0) + (dueDateFilter.length > 0 ? 1 : 0);

    const clearAllFilters = () => {
        setEntityFilter([]);
        setDueDateFilter([]);
    };

    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    marginBottom: 8,
                }}
            >
                <div>
                    <div className="chart-title-row1">
                        <h3 className="chart-title1">
                            {title}
                        </h3>
                    </div>
                    <span className="chart-refresh-time1">
                        Last Refreshed: {lastRefreshTime}
                    </span>
                </div>

                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                    }}
                >
                    <div
                        onClick={() => setIsDataViewOpen(true)}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 32,
                            height: 32,
                            borderRadius: 4,
                            border: '1px solid #d0d0d0',
                            cursor: 'pointer',
                            backgroundColor: '#fff',
                        }}
                        title="Data Points"
                    >
                        <DatasetLinkedIcon sx={{ fontSize: 16, color: '#555' }} />
                    </div>
                    <div
                        id="due-date-filter-icon-trigger"
                        onClick={() => setIsFilterPopoverOpen((curr) => !curr)}
                        className={`fs-chart-filter-trigger${activeFilterCount > 0 ? ' fs-chart-filter-trigger--active' : ''}`}
                        title="Filters"
                    >
                        <i
                            className="dx-icon-filter"
                            style={{ fontSize: 16, color: '#555' }}
                            aria-hidden="true"
                        />

                        {activeFilterCount > 0 && (
                            <span
                                style={{
                                    position: 'absolute',
                                    top: -4,
                                    right: -4,
                                    minWidth: 16,
                                    height: 16,
                                    borderRadius: 8,
                                    backgroundColor: '#1976d2',
                                    color: '#fff',
                                    fontSize: 10,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '0 3px',
                                }}
                            >
                                {activeFilterCount}
                            </span>
                        )}
                    </div>
                    {onRefresh && (
                        <div
                            onClick={onRefresh}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 32,
                                height: 32,
                                borderRadius: 4,
                                border: '1px solid #d0d0d0',
                                cursor: 'pointer',
                                backgroundColor: '#fff',
                            }}
                            title="Refresh"
                        >
                            <i
                                className="dx-icon-refresh"
                                style={{ fontSize: 16, color: '#555' }}
                                aria-hidden="true"
                            />
                        </div>
                    )}
                </div>
            </div>

            <Popover
                target="#due-date-filter-icon-trigger"
                visible={isFilterPopoverOpen}
                onHiding={() => setIsFilterPopoverOpen(false)}
                hideOnOutsideClick={true}
                position="bottom"
                width={280}
            >
                <div className="fs-chart-filter-content" style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 4 }}>
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Entity</div>
                        <TagBox
                            dataSource={entityOptions}
                            value={entityFilter}
                            onValueChanged={(e) => setEntityFilter(e.value ?? [])}
                            placeholder="All entities"
                            showSelectionControls={true}
                            applyValueMode="instantly"
                            searchEnabled={true}
                            width="100%"
                        />
                    </div>
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Due Date</div>
                        <TagBox
                            dataSource={dueDateOptions}
                            value={dueDateFilter}
                            onValueChanged={(e) => setDueDateFilter(e.value ?? [])}
                            placeholder="All due dates"
                            showSelectionControls={true}
                            applyValueMode="instantly"
                            searchEnabled={true}
                            width="100%"
                        />
                    </div>
                    {activeFilterCount > 0 && (
                        <button
                            type="button"
                            onClick={clearAllFilters}
                            style={{
                                fontSize: 12,
                                alignSelf: 'flex-start',
                                background: 'none',
                                border: 'none',
                                color: '#1976d2',
                                cursor: 'pointer',
                                padding: 0,
                            }}
                        >
                            Clear all filters
                        </button>
                    )}
                </div>
            </Popover>

            <DeliverablesDataPointsDialog
                open={isDataViewOpen}
                onClose={() => setIsDataViewOpen(false)}
                title={`${title || 'Deliverables - Data Points'}`}
                rows={filteredData}
            />

            {
                chartData?.length > 0 ? (
                    <>
                        <Chart id="status-by-due-date-chart" dataSource={chartData} rotated={false} onLegendClick={handleLegendClick} onPointClick={handlePointClick}>
                            <CommonSeriesSettings argumentField="federalDueDate" type="bar" barWidth={70} />
                            {entitySeries.map((entityName, index) => (
                                <Series key={entityName} valueField={entityName} name={entityName} color={bluePalette[index % bluePalette.length]}
                                    visible={selectedSeries.length === 0 || selectedSeries.includes(entityName)}
                                    showInLegend={!isLegendOverflowing || index < maxVisibleLegendItems} />
                            ))}

                            <ValueAxis position="left"><Title text="Count" /></ValueAxis>

                            <ArgumentAxis><Title text="Federal Due Date" /></ArgumentAxis>

                            <Legend verticalAlignment="bottom" horizontalAlignment="center" itemTextPosition="top" columnCount={legendColumns} rowCount={legendRows} customizeText={customizeEntityLegendText} customizeHint={customizeEntityLegendHint} />

                            <Export enabled={false} />
                            <Tooltip enabled={true} customizeTooltip={customizeCountTooltip} />
                        </Chart>

                        {isLegendOverflowing && (
                            <div className="legend-more-wrapper" style={{ textAlign: 'center', marginTop: 4 }}>
                                <button
                                    type="button"
                                    className="legend-more-btn"
                                    onClick={() => setIsLegendPopupOpen(true)}
                                >
                                    +{entitySeries.length - maxVisibleLegendItems} more
                                </button>
                            </div>
                        )}

                        <Popup
                            visible={isLegendPopupOpen}
                            onHiding={() => setIsLegendPopupOpen(false)}
                            title="All Entities"
                            showCloseButton={true}
                            width={400}
                            height="auto"
                            dragEnabled={false}
                        >
                            <div className="legend-popup-list" style={{ display: 'flex', alignItems: 'flex-start', gap: 12, overflowX: 'auto' }}>
                                {splitLegendItemsIntoColumns(entitySeries).map((column, columnIndex) => (
                                    <div key={`due-date-col-${columnIndex}`} className="legend-popup-column" style={{ display: 'flex', flexDirection: 'column', minWidth: 220 }}>
                                        {column.map((entityName, itemIndex) => {
                                            const isSelected = selectedSeries.length === 0 || selectedSeries.includes(entityName);
                                            const legendColor = getLegendPopupItemColor(columnIndex, itemIndex);
                                            return (
                                                <div
                                                    key={entityName}
                                                    className="legend-popup-item"
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        padding: '6px 8px',
                                                        cursor: 'pointer',
                                                        opacity: isSelected ? 1 : 0.4,
                                                    }}
                                                    onClick={() =>
                                                        setSelectedSeries((curr) =>
                                                            curr.includes(entityName)
                                                                ? curr.filter((n) => n !== entityName)
                                                                : [...curr, entityName]
                                                        )
                                                    }
                                                >
                                                    <span
                                                        aria-hidden={true}
                                                        style={{
                                                            width: 10,
                                                            height: 10,
                                                            borderRadius: 2,
                                                            backgroundColor: legendColor,
                                                            marginRight: 8,
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                    <span>{entityName}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ))}
                            </div>
                        </Popup>
                    </>
                ) : (
                    <div className="no-data-message">
                        No data available
                    </div>
                )
            }
        </div>
    );
}

export function Deliverables_Status_Before_915({ rawDeliverablesData, siteId, lastRefreshTime, onRefresh, title }: Props) {
    const [entityFilter, setEntityFilter] = useState<string[]>([]);
    const [dueDateFilter, setDueDateFilter] = useState<string[]>([]);
    const [isFilterPopoverOpen, setIsFilterPopoverOpen] = useState(false);
    const [isDataViewOpen, setIsDataViewOpen] = useState(false);

    const sourceData = rawDeliverablesData ?? [];

    const filters = {
        entity: (row: any) => entityFilter.length === 0 || entityFilter.includes(resolveEntityName(row)),
        dueDate: (row: any) => dueDateFilter.length === 0 || dueDateFilter.includes(getDeliverableDueDateValue(row)),
    };
    const entityOptions = getEntityFilterValues(filterReportingRows(sourceData, filters, 'entity'));
    const dueDateOptions = getDueDateFilterValues(filterReportingRows(sourceData, filters, 'dueDate'));
    const filteredData = filterReportingRows(sourceData, filters);

    const chartData = getStatusBeforeDate(filteredData, new Date('2027-09-15'));
    const sortedChartData = [...chartData].sort((a, b) => Number(b.value) - Number(a.value));
    const legendEntities = sortedChartData.map((x: any) => String(x?.entityName || ''));
    const {
        legendColumns,
        maxVisibleLegendItems,
        isLegendOverflowing,
        isLegendPopupOpen,
        setIsLegendPopupOpen,
    } = useOverflowLegend(legendEntities);
    const visibleLegendKeys = new Set(
        isLegendOverflowing ? legendEntities.slice(0, maxVisibleLegendItems) : legendEntities
    );
    const {
        handleLegendClick,
        customizeLegendItems,
        toggleStatus,
        isStatusSelected,
        setChartComponent,
    } = usePieLegendSliceToggle(visibleLegendKeys);
    const handlePointClick = (event: any) => handleDeliverablesPointClick(event, siteId, DeliverableColumns.ENTITY_NAME, undefined, rawDeliverablesData);

    const activeFilterCount = (entityFilter.length > 0 ? 1 : 0) + (dueDateFilter.length > 0 ? 1 : 0);

    const clearAllFilters = () => {
        setEntityFilter([]);
        setDueDateFilter([]);
    };

    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    marginBottom: 8,
                }}
            >
                {/* Left Section */}
                <div>
                    <div className="chart-title-row1">
                        <h3 className="chart-title1">
                            {title}
                        </h3>
                    </div>
                    <span className="chart-refresh-time1">
                        Last Refreshed: {lastRefreshTime}
                    </span>
                </div>

                {/* Right Section */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                    }}
                >
                    <div
                        onClick={() => setIsDataViewOpen(true)}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 32,
                            height: 32,
                            borderRadius: 4,
                            border: '1px solid #d0d0d0',
                            cursor: 'pointer',
                            backgroundColor: '#fff',
                        }}
                        title="Data Points"
                    >
                        <DatasetLinkedIcon sx={{ fontSize: 16, color: '#555' }} />
                    </div>
                    <div
                        id="before-915-filter-icon-trigger"
                        onClick={() => setIsFilterPopoverOpen((curr) => !curr)}
                        className={`fs-chart-filter-trigger${activeFilterCount > 0 ? ' fs-chart-filter-trigger--active' : ''}`}
                        title="Filters"
                    >
                        <i
                            className="dx-icon-filter"
                            style={{ fontSize: 16, color: '#555' }}
                            aria-hidden="true"
                        />

                        {activeFilterCount > 0 && (
                            <span
                                style={{
                                    position: 'absolute',
                                    top: -4,
                                    right: -4,
                                    minWidth: 16,
                                    height: 16,
                                    borderRadius: 8,
                                    backgroundColor: '#1976d2',
                                    color: '#fff',
                                    fontSize: 10,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '0 3px',
                                }}
                            >
                                {activeFilterCount}
                            </span>
                        )}
                    </div>
                    {onRefresh && (
                        <div
                            onClick={onRefresh}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 32,
                                height: 32,
                                borderRadius: 4,
                                border: '1px solid #d0d0d0',
                                cursor: 'pointer',
                                backgroundColor: '#fff',
                            }}
                            title="Refresh"
                        >
                            <i
                                className="dx-icon-refresh"
                                style={{ fontSize: 16, color: '#555' }}
                                aria-hidden="true"
                            />
                        </div>
                    )}
                </div>
            </div>
            <Popover
                target="#before-915-filter-icon-trigger"
                visible={isFilterPopoverOpen}
                onHiding={() => setIsFilterPopoverOpen(false)}
                hideOnOutsideClick={true}
                position="bottom"
                width={280}
            >
                <div className="fs-chart-filter-content" style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 4 }}>
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Entity</div>
                        <TagBox
                            dataSource={entityOptions}
                            value={entityFilter}
                            onValueChanged={(e) => setEntityFilter(e.value ?? [])}
                            placeholder="All entities"
                            showSelectionControls={true}
                            applyValueMode="instantly"
                            searchEnabled={true}
                            width="100%"
                        />
                    </div>
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Due Date</div>
                        <TagBox
                            dataSource={dueDateOptions}
                            value={dueDateFilter}
                            onValueChanged={(e) => setDueDateFilter(e.value ?? [])}
                            placeholder="All due dates"
                            showSelectionControls={true}
                            applyValueMode="instantly"
                            searchEnabled={true}
                            width="100%"
                        />
                    </div>
                    {activeFilterCount > 0 && (
                        <button
                            type="button"
                            onClick={clearAllFilters}
                            style={{
                                fontSize: 12,
                                alignSelf: 'flex-start',
                                background: 'none',
                                border: 'none',
                                color: '#1976d2',
                                cursor: 'pointer',
                                padding: 0,
                            }}
                        >
                            Clear all filters
                        </button>
                    )}
                </div>
            </Popover>

            <DeliverablesDataPointsDialog
                open={isDataViewOpen}
                onClose={() => setIsDataViewOpen(false)}
                title={`${title || 'Deliverables - Data Points'} `}
                rows={filteredData}
            />

            {
                sortedChartData?.length > 0 ? (
                    <>
                        <PieChart dataSource={sortedChartData} type="doughnut" innerRadius={0.5} resolveLabelOverlapping="shift" palette={bluePalette} onLegendClick={handleLegendClick} onPointClick={handlePointClick} onDrawn={(e: any) => setChartComponent(e?.component)}                        >
                            <PieSeries argumentField="entityName" valueField="value">
                                <Label visible={true} customizeText={customizePieLabel} radialOffset={-20} font={{ color: "#000000" }} backgroundColor="transparent"                                >
                                    <Connector visible={true} width={1} />
                                </Label>
                            </PieSeries>

                            <PieLegend verticalAlignment="bottom" horizontalAlignment="center" itemTextPosition="right" markerSize={14} columnCount={legendColumns} customizeText={customizePieLegendText} customizeHint={customizeEntityLegendHint} customizeItems={customizeLegendItems} />
                            <PieTooltip enabled={true} customizeTooltip={customizePieTooltip} />
                            <PieExport enabled={false} />
                        </PieChart>
                        {isLegendOverflowing && (
                            <div className="legend-more-wrapper" style={{ textAlign: 'center', marginTop: 4 }}>
                                <button
                                    type="button"
                                    className="legend-more-btn"
                                    onClick={() => setIsLegendPopupOpen(true)}
                                >
                                    +{legendEntities.length - maxVisibleLegendItems} more
                                </button>
                            </div>
                        )}

                        <Popup
                            visible={isLegendPopupOpen}
                            onHiding={() => setIsLegendPopupOpen(false)}
                            title="All Entities"
                            showCloseButton={true}
                            width={400}
                            height="auto"
                            dragEnabled={false}
                        >
                            <div className="legend-popup-list" style={{ display: 'flex', alignItems: 'flex-start', gap: 12, overflowX: 'auto' }}>
                                {splitLegendItemsIntoColumns(legendEntities).map((column, columnIndex) => (
                                    <div key={`before-col-${columnIndex}`} className="legend-popup-column" style={{ display: 'flex', flexDirection: 'column', minWidth: 220 }}>
                                        {column.map((entityName, itemIndex) => (
                                            <div
                                                key={entityName}
                                                className="legend-popup-item"
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    padding: '6px 8px',
                                                    cursor: 'pointer',
                                                    opacity: isStatusSelected(entityName) ? 1 : 0.4,
                                                }}
                                                onClick={() => toggleStatus(entityName)}
                                            >
                                                <span
                                                    aria-hidden={true}
                                                    style={{
                                                        width: 10,
                                                        height: 10,
                                                        borderRadius: 2,
                                                        backgroundColor: getLegendPopupItemColor(columnIndex, itemIndex),
                                                        marginRight: 8,
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <span>{entityName}</span>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </Popup>
                    </>
                ) : (
                    <div className="no-data-message">
                        No data available
                    </div>
                )
            }
        </div>
    );
}

export function Deliverables_Status_after_915({ rawDeliverablesData, siteId, title, lastRefreshTime, onRefresh }: Props) {
    const [entityFilter, setEntityFilter] = useState<string[]>([]);
    const [dueDateFilter, setDueDateFilter] = useState<string[]>([]);
    const [isFilterPopoverOpen, setIsFilterPopoverOpen] = useState(false);
    const [isDataViewOpen, setIsDataViewOpen] = useState(false);

    const sourceData = rawDeliverablesData ?? [];

    const filters = {
        entity: (row: any) => entityFilter.length === 0 || entityFilter.includes(resolveEntityName(row)),
        dueDate: (row: any) => dueDateFilter.length === 0 || dueDateFilter.includes(getDeliverableDueDateValue(row)),
    };
    const entityOptions = getEntityFilterValues(filterReportingRows(sourceData, filters, 'entity'));
    const dueDateOptions = getDueDateFilterValues(filterReportingRows(sourceData, filters, 'dueDate'));
    const filteredData = filterReportingRows(sourceData, filters);

    const chartData = getStatusGrouped(filteredData);
    const legendEntities = chartData.map((x: any) => String(x?.entityName || ''));
    const {
        legendColumns,
        maxVisibleLegendItems,
        isLegendOverflowing,
        isLegendPopupOpen,
        setIsLegendPopupOpen,
    } = useOverflowLegend(legendEntities);
    const visibleLegendKeys = new Set(
        isLegendOverflowing ? legendEntities.slice(0, maxVisibleLegendItems) : legendEntities
    );
    const {
        handleLegendClick,
        customizeLegendItems,
        toggleStatus,
        isStatusSelected,
        setChartComponent,
    } = usePieLegendSliceToggle(visibleLegendKeys);
    const handlePointClick = (event: any) => handleDeliverablesPointClick(event, siteId, DeliverableColumns.ENTITY_NAME, undefined, rawDeliverablesData);

    const activeFilterCount = (entityFilter.length > 0 ? 1 : 0) + (dueDateFilter.length > 0 ? 1 : 0);

    const clearAllFilters = () => {
        setEntityFilter([]);
        setDueDateFilter([]);
    };

    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    marginBottom: 8,
                }}
            >
                {/* Left Section */}
                <div>
                    <div className="chart-title-row1">
                        <h3 className="chart-title1">
                            {title}
                        </h3>
                    </div>
                    <span className="chart-refresh-time1">
                        Last Refreshed: {lastRefreshTime}
                    </span>
                </div>

                {/* Right Section */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                    }}
                >
                    <div
                        onClick={() => setIsDataViewOpen(true)}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 32,
                            height: 32,
                            borderRadius: 4,
                            border: '1px solid #d0d0d0',
                            cursor: 'pointer',
                            backgroundColor: '#fff',
                        }}
                        title="Data Points"
                    >
                        <DatasetLinkedIcon sx={{ fontSize: 16, color: '#555' }} />
                    </div>
                    <div
                        id="after-915-filter-icon-trigger"
                        onClick={() => setIsFilterPopoverOpen((curr) => !curr)}
                        className={`fs-chart-filter-trigger${activeFilterCount > 0 ? ' fs-chart-filter-trigger--active' : ''}`}
                        title="Filters"
                    >
                        <i
                            className="dx-icon-filter"
                            style={{ fontSize: 16, color: '#555' }}
                            aria-hidden="true"
                        />

                        {activeFilterCount > 0 && (
                            <span
                                style={{
                                    position: 'absolute',
                                    top: -4,
                                    right: -4,
                                    minWidth: 16,
                                    height: 16,
                                    borderRadius: 8,
                                    backgroundColor: '#1976d2',
                                    color: '#fff',
                                    fontSize: 10,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '0 3px',
                                }}
                            >
                                {activeFilterCount}
                            </span>
                        )}
                    </div>
                    {onRefresh && (
                        <div
                            onClick={onRefresh}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 32,
                                height: 32,
                                borderRadius: 4,
                                border: '1px solid #d0d0d0',
                                cursor: 'pointer',
                                backgroundColor: '#fff',
                            }}
                            title="Refresh"
                        >
                            <i
                                className="dx-icon-refresh"
                                style={{ fontSize: 16, color: '#555' }}
                                aria-hidden="true"
                            />
                        </div>
                    )}
                </div>
            </div>
            <Popover
                target="#after-915-filter-icon-trigger"
                visible={isFilterPopoverOpen}
                onHiding={() => setIsFilterPopoverOpen(false)}
                hideOnOutsideClick={true}
                position="bottom"
                width={280}
            >
                <div className="fs-chart-filter-content" style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 4 }}>
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Entity</div>
                        <TagBox
                            dataSource={entityOptions}
                            value={entityFilter}
                            onValueChanged={(e) => setEntityFilter(e.value ?? [])}
                            placeholder="All entities"
                            showSelectionControls={true}
                            applyValueMode="instantly"
                            searchEnabled={true}
                            width="100%"
                        />
                    </div>
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Due Date</div>
                        <TagBox
                            dataSource={dueDateOptions}
                            value={dueDateFilter}
                            onValueChanged={(e) => setDueDateFilter(e.value ?? [])}
                            placeholder="All due dates"
                            showSelectionControls={true}
                            applyValueMode="instantly"
                            searchEnabled={true}
                            width="100%"
                        />
                    </div>
                    {activeFilterCount > 0 && (
                        <button
                            type="button"
                            onClick={clearAllFilters}
                            style={{
                                fontSize: 12,
                                alignSelf: 'flex-start',
                                background: 'none',
                                border: 'none',
                                color: '#1976d2',
                                cursor: 'pointer',
                                padding: 0,
                            }}
                        >
                            Clear all filters
                        </button>
                    )}
                </div>
            </Popover>

            <DeliverablesDataPointsDialog
                open={isDataViewOpen}
                onClose={() => setIsDataViewOpen(false)}
                title={`${title || 'Deliverables - Data Points'} `}
                rows={filteredData}
            />

            {
                chartData?.length > 0 ? (
                    <>
                        <PieChart palette={bluePalette} dataSource={chartData} type="doughnut" innerRadius={0.5} resolveLabelOverlapping="shift" onLegendClick={handleLegendClick} onPointClick={handlePointClick} onDrawn={(e: any) => setChartComponent(e?.component)}>
                            <PieSeries argumentField="entityName" valueField="value">
                                <Label visible={true} customizeText={customizePieLabel} radialOffset={-20} font={{ color: '#000000' }} backgroundColor="transparent">
                                    <Connector visible={true} width={1} />
                                </Label>
                            </PieSeries>
                            <PieLegend verticalAlignment="bottom" horizontalAlignment="center" itemTextPosition="right" markerSize={14} columnCount={legendColumns} customizeText={customizePieLegendText} customizeHint={customizeEntityLegendHint} customizeItems={customizeLegendItems} />
                            <PieTooltip enabled={true} customizeTooltip={customizePieTooltip} />
                            <PieExport enabled={false} />
                        </PieChart>

                        {isLegendOverflowing && (
                            <div className="legend-more-wrapper" style={{ textAlign: 'center', marginTop: 4 }}>
                                <button
                                    type="button"
                                    className="legend-more-btn"
                                    onClick={() => setIsLegendPopupOpen(true)}
                                >
                                    +{legendEntities.length - maxVisibleLegendItems} more
                                </button>
                            </div>
                        )}

                        <Popup
                            visible={isLegendPopupOpen}
                            onHiding={() => setIsLegendPopupOpen(false)}
                            title="All Entities"
                            showCloseButton={true}
                            width={400}
                            height="auto"
                            dragEnabled={false}
                        >
                            <div className="legend-popup-list" style={{ display: 'flex', alignItems: 'flex-start', gap: 12, overflowX: 'auto' }}>
                                {splitLegendItemsIntoColumns(legendEntities).map((column, columnIndex) => (
                                    <div key={`after-col-${columnIndex}`} className="legend-popup-column" style={{ display: 'flex', flexDirection: 'column', minWidth: 220 }}>
                                        {column.map((entityName, itemIndex) => (
                                            <div
                                                key={entityName}
                                                className="legend-popup-item"
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    padding: '6px 8px',
                                                    cursor: 'pointer',
                                                    opacity: isStatusSelected(entityName) ? 1 : 0.4,
                                                }}
                                                onClick={() => toggleStatus(entityName)}
                                            >
                                                <span
                                                    aria-hidden={true}
                                                    style={{
                                                        width: 10,
                                                        height: 10,
                                                        borderRadius: 2,
                                                        backgroundColor: getLegendPopupItemColor(columnIndex, itemIndex),
                                                        marginRight: 8,
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <span>{entityName}</span>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </Popup>
                    </>
                ) : (
                    <div className="no-data-message">
                        No data available
                    </div>
                )
            }
        </div>
    );
}